import { Loading } from '@/components/atoms/Loading';
import { MainCard } from '@/components/molecules/MainCard';
import {
  Box,
  Button,
  Divider,
  Grid,
  Typography,
  Dialog,
  useTheme,
} from '@mui/material';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ShiftAccordion } from '@/components/molecules/ShiftAccordion';
import { Add, Close, DragIndicator } from '@mui/icons-material';
import { ShiftForm } from '@/components/forms/ShiftForm';
import { useModal } from 'react-modal-hook';
import { useCreateShiftMutation } from '@/hooks/useCreateShiftMutation';
import { useSnackbar } from 'notistack';
import { useShiftsQuery } from '@/hooks/useShiftsQuery';
import { useShiftRerankMutation } from '@/hooks/useShiftRerankMutation';
import { useAuth } from '@/providers/AuthProvider';
import type { Shift } from '@/openapi';

interface SortableShiftRowProps {
  shift: Shift;
  disabled: boolean;
}

const SortableShiftRow: React.FC<SortableShiftRowProps> = ({
  shift,
  disabled,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: shift.id, disabled });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 1 : 'auto',
    position: 'relative',
  };
  return (
    <div ref={setNodeRef} style={style}>
      <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
        <Box
          ref={setActivatorNodeRef}
          {...attributes}
          {...listeners}
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 0.5,
            cursor: disabled ? 'default' : 'grab',
            color: 'text.secondary',
            touchAction: 'none',
            '&:active': { cursor: disabled ? 'default' : 'grabbing' },
            visibility: disabled ? 'hidden' : 'visible',
          }}
          aria-label="Drag to reorder shift">
          <DragIndicator fontSize="small" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ShiftAccordion shift={shift} />
        </Box>
      </Box>
      <Divider />
    </div>
  );
};

export const Shifts: React.FC = () => {
  const { isLoading: isLoadingShifts, data: shifts } = useShiftsQuery();
  const rerankMutation = useShiftRerankMutation();
  const { isAdmin } = useAuth();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !shifts) {
      return;
    }
    const startIndex = shifts.findIndex((s) => s.id === active.id);
    const endIndex = shifts.findIndex((s) => s.id === over.id);
    if (startIndex === -1 || endIndex === -1) {
      return;
    }
    rerankMutation.mutateAsync({
      startIndex,
      endIndex,
      subjectShiftId: shifts[startIndex].id,
      targetShiftId: shifts[endIndex].id,
    });
  };
  const theme = useTheme();
  const snackbar = useSnackbar();

  const defaultShift: Shift = {
    id: 0,
    description: '',
    rank: 0,
    onCall: false,
    deleted: false,
    standard: true,
    monday: false,
    mondaypoints: 0,
    mondaycutpoints: 0,
    tuesday: false,
    tuesdaypoints: 0,
    tuesdaycutpoints: 0,
    wednesday: false,
    wednesdaypoints: 0,
    wednesdaycutpoints: 0,
    thursday: false,
    thursdaypoints: 0,
    thursdaycutpoints: 0,
    friday: false,
    fridaypoints: 0,
    fridaycutpoints: 0,
    saturday: false,
    saturdaypoints: 0,
    saturdaycutpoints: 0,
    sunday: false,
    sundaypoints: 0,
    sundaycutpoints: 0,
  };

  const shiftCreateMutation = useCreateShiftMutation();
  const createShift = async (shift: Shift) => {
    try {
      await shiftCreateMutation.mutateAsync(shift);
      snackbar.enqueueSnackbar('Shift created successfully', {
        variant: 'success',
      });
    } catch {
      snackbar.enqueueSnackbar('Failed to create shift', {
        variant: 'error',
      });
    }
    hideModal();
  };

  const [showModal, hideModal] = useModal(() => (
    <Dialog open={true} fullWidth maxWidth="md">
      <Grid
        container
        sx={{
          justifyContent: 'right',
          width: '100%',
        }}>
        <Button onClick={hideModal}>
          <Close />
        </Button>
      </Grid>
      <Typography
        variant="h2"
        sx={{
          textAlign: 'center',
        }}>
        Create New Shift
      </Typography>
      <Box
        sx={{
          py: 4,
          width: '100%',
          maxWidth: theme.spacing(120),
        }}>
        <ShiftForm
          mode="modal"
          shift={defaultShift}
          onSubmit={createShift}
          onCancel={hideModal}
        />
      </Box>
    </Dialog>
  ));

  return (
    <Grid size={12}>
      <Typography
        variant="h1"
        sx={{
          textAlign: 'center',
        }}>
        Edit Shifts
      </Typography>
      <Box
        sx={{
          m: 4,
        }}
      />
      <MainCard>
        <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 0.5,
              visibility: 'hidden',
            }}
            aria-hidden>
            <DragIndicator fontSize="small" />
          </Box>
          <Grid
            container
            sx={{
              alignItems: 'baseline',
              pl: 2,
              pr: 5,
              mb: 2,
              flex: 1,
              minWidth: 0,
            }}>
            <Grid
              size={{
                xs: 2,
                md: 1,
              }}>
              <Typography variant="h4">Std</Typography>
            </Grid>
            <Grid
              size={{
                xs: 4,
                md: 5,
              }}>
              <Typography variant="h4">Name</Typography>
            </Grid>
            <Grid
              size={{
                xs: 2,
                md: 2,
              }}>
              <Typography variant="h4">Points</Typography>
            </Grid>
            <Grid
              size={{
                xs: 2,
                md: 3,
              }}>
              <Typography variant="h4">Days</Typography>
            </Grid>
            <Grid
              size={{
                xs: 2,
                md: 1,
              }}>
              <Button variant="contained" size="small" onClick={showModal}>
                <Add />
                New
              </Button>
            </Grid>
            <Grid
              size={{
                xs: 2,
                md: 1,
              }}>
              <Box />
            </Grid>
          </Grid>
        </Box>
        {shifts ? (
          <DndContext sensors={sensors} onDragEnd={onDragEnd}>
            <SortableContext
              items={shifts.map((s) => s.id)}
              strategy={verticalListSortingStrategy}>
              <div>
                {shifts.map((shift) => (
                  <SortableShiftRow
                    key={shift.id}
                    shift={shift}
                    disabled={!isAdmin}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : isLoadingShifts ? (
          <Loading />
        ) : (
          'Shifts not found.'
        )}
      </MainCard>
      <Box
        sx={{
          m: 6,
        }}
      />
    </Grid>
  );
};
